import { useId, type JSX } from 'react';
import type { SupportedCountry } from '@archipro-website/localisation';
import { cn } from '@rocco/utils/cn';

const NAVY = '#012169';
const RED = '#C8102E';
const WHITE = '#FFFFFF';

/** SVG `points` for an n-pointed star centred on (cx, cy), first point up. */
const starPoints = (
    points: number,
    cx: number,
    cy: number,
    radius: number,
    innerRatio: number,
): string =>
    Array.from({ length: points * 2 }, (_, i) => {
        const r = i % 2 === 0 ? radius : radius * innerRatio;
        const angle = (Math.PI * i) / points - Math.PI / 2;
        return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
    }).join(' ');

/** The 50 stars of the US canton: nine rows alternating six and five. */
const US_STARS = Array.from({ length: 9 }, (_, row) => {
    const count = row % 2 === 0 ? 6 : 5;
    const offset = row % 2 === 0 ? 1 : 2;
    return Array.from({ length: count }, (_, column) =>
        starPoints(5, (offset + column * 2) * 247, (row + 1) * 210, 120, 0.382),
    );
}).flat();

const UsFlag = (): JSX.Element => (
    <svg viewBox="0 0 7410 3900">
        <rect width="7410" height="3900" fill="#B31942" />
        {Array.from({ length: 6 }, (_, i) => (
            <rect
                key={i}
                y={(i * 2 + 1) * 300}
                width="7410"
                height="300"
                fill={WHITE}
            />
        ))}
        <rect width="2964" height="2100" fill="#0A3161" />
        {US_STARS.map((points) => (
            <polygon key={points} points={points} fill={WHITE} />
        ))}
    </svg>
);

/** The Union Jack, drawn into the top-left 60x30 of a 120x60 flag. */
const UnionJack = ({ id }: { id: string }): JSX.Element => (
    <>
        <clipPath id={`${id}-canton`}>
            <path d="M0,0 v30 h60 v-30 z" />
        </clipPath>
        <clipPath id={`${id}-saltire`}>
            <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <g clipPath={`url(#${id}-canton)`}>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke={WHITE} strokeWidth="6" />
            <path
                d="M0,0 L60,30 M60,0 L0,30"
                clipPath={`url(#${id}-saltire)`}
                stroke={RED}
                strokeWidth="4"
            />
            <path d="M30,0 v30 M0,15 h60" stroke={WHITE} strokeWidth="10" />
            <path d="M30,0 v30 M0,15 h60" stroke={RED} strokeWidth="6" />
        </g>
    </>
);

/** Southern Cross as it appears on the New Zealand flag: red with a white edge. */
const NZ_STARS: Array<[number, number, number]> = [
    [90, 12, 4],
    [75.8, 26.7, 4.5],
    [102.9, 22.9, 4],
    [90, 48, 5],
];

const NzFlag = ({ id }: { id: string }): JSX.Element => (
    <svg viewBox="0 0 120 60">
        <rect width="120" height="60" fill={NAVY} />
        <UnionJack id={id} />
        {NZ_STARS.map(([cx, cy, r]) => (
            <g key={`${cx}-${cy}`}>
                <polygon
                    points={starPoints(5, cx, cy, r * 1.3, 0.382)}
                    fill={WHITE}
                />
                <polygon points={starPoints(5, cx, cy, r, 0.382)} fill={RED} />
            </g>
        ))}
    </svg>
);

/** Commonwealth Star plus the Southern Cross, as on the Australian flag. */
const AU_STARS: Array<[number, number, number, number]> = [
    [30, 45, 9, 7],
    [90, 50, 4.3, 7],
    [75, 26.25, 4.3, 7],
    [90, 10, 4.3, 7],
    [103.3, 22.25, 4.3, 7],
    [96, 32.5, 2.5, 5],
];

const AuFlag = ({ id }: { id: string }): JSX.Element => (
    <svg viewBox="0 0 120 60">
        <rect width="120" height="60" fill={NAVY} />
        <UnionJack id={id} />
        {AU_STARS.map(([cx, cy, r, points]) => (
            <polygon
                key={`${cx}-${cy}`}
                points={starPoints(
                    points,
                    cx,
                    cy,
                    r,
                    points === 7 ? 4 / 9 : 0.382,
                )}
                fill={WHITE}
            />
        ))}
    </svg>
);

const FLAGS: Record<SupportedCountry, (props: { id: string }) => JSX.Element> =
    {
        NZ: NzFlag,
        AU: AuFlag,
        US: UsFlag,
    };

interface CountryFlagProps {
    country: SupportedCountry;
    className?: string;
}

/** Decorative market flag; the surrounding text names the country. */
export const CountryFlag = ({
    country,
    className,
}: CountryFlagProps): JSX.Element => {
    // Clip path ids must be unique per page and valid inside `url(#...)`.
    const id = `flag${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
    const Flag = FLAGS[country];
    return (
        <span
            aria-hidden="true"
            className={cn(
                'inline-flex shrink-0 overflow-hidden rounded-xs [&>svg]:h-full [&>svg]:w-auto',
                className,
            )}
        >
            <Flag id={id} />
        </span>
    );
};
